import { createClient } from '@supabase/supabase-js';
import { Capacitor } from '@capacitor/core';

// In native apps, OAuth must redirect to the custom deep-link scheme.
// In web, use the current origin so hosted deployments work without env changes.
// Scheme is lowercase on purpose: Supabase/Google normalize the redirect scheme to
// lowercase and Android matches deep-link schemes case-sensitively, so the registered
// intent filter and this URL must both be lowercase (com.malkkov.volyx).
export const authRedirectUrl = Capacitor.isNativePlatform()
  ? 'com.malkkov.volyx://auth/callback'
  : `${typeof window !== 'undefined' ? window.location.origin : ''}/auth/callback`;

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Configuration validation
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase configuration. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file'
  );
}

// Warn about placeholder values in development
if (import.meta.env.DEV) {
  if (
    supabaseUrl.includes('your-project-ref') ||
    supabaseAnonKey.includes('your-anon-key')
  ) {
    console.warn(
      '⚠️ Supabase placeholder values detected. Update .env with your actual credentials:\n' +
      '1. Go to Supabase dashboard → Project Settings → API\n' +
      '2. Copy Project URL and anon/public key\n' +
      '3. Update VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY'
    );
  }
}

// Custom fetch in dev to log failed requests and hint at common causes
const customFetch: typeof fetch = import.meta.env.DEV
  ? async (input, init) => {
      const url = typeof input === 'string' ? input : input instanceof Request ? input.url : String(input);
      try {
        return await fetch(input, init);
      } catch (err) {
        console.error(
          '[Supabase] Request failed:',
          url,
          err instanceof Error ? err.message : err,
          '\n• If using placeholders in .env, replace with real Project URL + anon key from Supabase → Settings → API',
          '\n• Free-tier projects pause after inactivity: open Supabase dashboard and resume if needed',
          '\n• Try disabling CORS/Privacy extensions or use an incognito window'
        );
        throw err;
      }
    }
  : fetch;

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  global: { fetch: customFetch },
  auth: {
    // Enable automatic session persistence
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true, // Enable for web OAuth redirects
    // PKCE returns a ?code= we can exchange manually after the native deep-link
    // redirect (com.malkkov.volyx://auth/callback). Web keeps using
    // detectSessionInUrl, which handles PKCE codes too.
    flowType: 'pkce',
    // Use localStorage for session persistence in web
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  },
  realtime: {
    // Configure realtime settings for better reliability
    params: {
      eventsPerSecond: 10,
    },
  },
});

// Dev only: expose client on window for console RLS checks
// e.g. await window.supabase.from('profiles').select('first_name').eq('id', uuid).maybeSingle()
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { supabase: typeof supabase }).supabase = supabase;
}
